import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuthCtx } from "@/lib/auth-context";
import { seo } from "@/lib/seo";
import { CardFormSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/esqueci-senha")({
  head: () =>
    seo({
      path: "/esqueci-senha",
      title: "Esqueci minha senha — Seu Bolso Tranquilo",
      description: "Receba um link por email para redefinir sua senha.",
      noindex: true,
    }),
  component: ForgotPasswordPage,
  pendingComponent: () => <CardFormSkeleton fields={1} />,
});

const schema = z.object({
  email: z.string().trim().email("Email inválido").max(255),
});

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPasswordForEmail } = useAuthCtx();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Email inválido");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await resetPasswordForEmail(parsed.data.email);
      if (error) {
        toast.error("Não foi possível enviar o email. Tente novamente.");
        return;
      }
      setSent(true);
      toast.success("Email enviado! Verifique sua caixa de entrada.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="size-4" /> Voltar para login
        </Link>

        <div className="rounded-3xl border border-border/40 bg-gradient-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <BrandLogo className="size-10" />
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight">Esqueci minha senha</h1>
              <p className="text-sm text-muted-foreground">Te enviaremos um link por email.</p>
            </div>
          </div>

          {sent ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto inline-flex p-3 rounded-2xl bg-primary/15 border border-primary/30">
                <MailCheck className="size-6 text-primary-glow" />
              </div>
              <p className="text-sm text-muted-foreground">
                Se existe uma conta com <strong className="text-foreground">{email}</strong>, você receberá um email com instruções para redefinir sua senha.
              </p>
              <button
                onClick={() => navigate({ to: "/auth" })}
                className="w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity"
              >
                Voltar para login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@email.com"
                  required
                  maxLength={255}
                  className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="size-4 animate-spin" />}
                Enviar link de redefinição
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}